import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import TagPickerField from "@/components/TagPickerField";

type Contact = Tables<"contacts">;

const LOVES_SUGGESTIONS = [
  "art & culture", "coffee", "family", "fashion & style", "fitness",
  "food & dining", "gaming", "music", "nature & hiking",
  "photography", "reading", "tech & gadgets", "travel",
  "wellness & mindfulness", "wine",
];

const DISLIKES_SUGGESTIONS = [
  "alcohol", "being late", "cheap gifts", "cold weather",
  "confrontation", "crowds", "early mornings", "fast fashion",
  "formal occasions", "loud environments", "seafood",
  "small talk", "social media", "spicy food", "surprises",
];

interface AboutScreenProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact;
  onSaved: () => void;
}

interface CharCounterProps {
  current: number;
  max: number;
  warnAt: number;
}

const CharCounter = ({ current, max, warnAt }: CharCounterProps) => (
  <p className={`text-right text-xs ${
    current >= max ? "text-destructive" : current >= warnAt ? "text-accent" : "text-muted-foreground"
  }`}>
    {current}/{max}
  </p>
);

const AboutScreen = ({ open, onOpenChange, contact, onSaved }: AboutScreenProps) => {
  const [personality, setPersonality] = useState("");
  const [loves, setLoves] = useState<string[]>([]);
  const [dislikes, setDislikes] = useState<string[]>([]);
  const [giftIdeas, setGiftIdeas] = useState("");
  const [sharedMemories, setSharedMemories] = useState("");
  const [otherNotes, setOtherNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Track initial values for dirty check
  const [initial, setInitial] = useState({
    personality: "", loves: [] as string[], dislikes: [] as string[],
    giftIdeas: "", sharedMemories: "", otherNotes: "",
  });

  useEffect(() => {
    if (open && contact) {
      const c = contact as any;
      const p = (c.personality ?? "").slice(0, 100);
      const l = c.loves ?? [];
      const d = c.dislikes ?? [];
      const g = c.gift_ideas ?? "";
      const s = c.shared_memories ?? "";
      const n = c.notes ?? "";
      setPersonality(p);
      setLoves(l);
      setDislikes(d);
      setGiftIdeas(g);
      setSharedMemories(s);
      setOtherNotes(n);
      setInitial({ personality: p, loves: l, dislikes: d, giftIdeas: g, sharedMemories: s, otherNotes: n });
    }
  }, [open, contact]);

  const isDirty = useMemo(() => {
    return personality !== initial.personality
      || JSON.stringify(loves) !== JSON.stringify(initial.loves)
      || JSON.stringify(dislikes) !== JSON.stringify(initial.dislikes)
      || giftIdeas !== initial.giftIdeas
      || sharedMemories !== initial.sharedMemories
      || otherNotes !== initial.otherNotes;
  }, [personality, loves, dislikes, giftIdeas, sharedMemories, otherNotes, initial]);

  const [showExitGuard, setShowExitGuard] = useState(false);

  const dismissActiveField = () => {
    if (typeof document === "undefined") return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  };

  const handleClose = () => {
    dismissActiveField();

    if (isDirty) {
      setShowExitGuard(true);
      return;
    }
    onOpenChange(false);
  };

  const handleSave = async () => {
    dismissActiveField();
    setSaving(true);
    const trimmedPersonality = personality.trim() || null;
    const trimmedLoves = loves.length > 0 ? loves : null;
    const trimmedDislikes = dislikes.length > 0 ? dislikes : null;
    const trimmedGifts = giftIdeas.trim() || null;
    const trimmedMemories = sharedMemories.trim() || null;
    const trimmedNotes = otherNotes.trim() || null;

    // If all enrichment data cleared, reset nudge so it reappears
    const allEmpty = !trimmedPersonality && !trimmedLoves && !trimmedDislikes && !trimmedGifts && !trimmedMemories && !trimmedNotes;

    const { error } = await supabase
      .from("contacts")
      .update({
        personality: trimmedPersonality,
        loves: trimmedLoves,
        dislikes: trimmedDislikes,
        gift_ideas: trimmedGifts,
        shared_memories: trimmedMemories,
        notes: trimmedNotes,
        ...(allEmpty ? { about_nudge_dismissed: false } : {}),
      } as any)
      .eq("id", contact.id);
    setSaving(false);

    if (error) {
      toast.error("Couldn't save. Please try again.");
      return;
    }

    toast.success(`${contact.full_name}'s profile updated 💛`);
    onOpenChange(false);
    onSaved();
  };

  const firstName = contact.full_name.split(" ")[0];

  return (
    <>
    <Drawer open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DrawerContent className="h-[92vh] rounded-t-2xl bg-[hsl(37,26%,91%)]">
        {/* Header */}
        <div className="relative px-5 pt-4 pb-2">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="font-serif text-2xl font-bold text-foreground">
            About
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything here helps you show up better for {firstName}.
          </p>
        </div>

        {/* Scrollable fields */}
        <div className="flex-1 overflow-y-auto px-5 pb-28">
          {/* Personality */}
          <div className="mt-6">
            <p className="text-sm font-bold text-foreground">Personality</p>
            <p className="mt-0.5 text-xs text-muted-foreground">How would you describe them?</p>
            <Textarea
              value={personality}
              onChange={(e) => setPersonality(e.target.value.slice(0, 100))}
              placeholder="e.g. Warm, dry humour, deeply loyal"
              rows={2}
              className="mt-2 rounded-2xl bg-background border border-input ring-offset-background focus-visible:ring-ring"
            />
            <CharCounter current={personality.length} max={100} warnAt={80} />
          </div>

          {/* Loves */}
          <div className="mt-6">
            <TagPickerField
              label="Loves"
              helper="What do they enjoy? Add up to 5."
              suggestedTags={LOVES_SUGGESTIONS}
              selectedTags={loves}
              onTagsChange={setLoves}
              maxTags={5}
              conflictTags={dislikes}
              conflictLabel="Dislikes"
            />
          </div>

          {/* Dislikes */}
          <div className="mt-6">
            <TagPickerField
              label="Dislikes"
              helper="What do they avoid or dislike? Add up to 5."
              suggestedTags={DISLIKES_SUGGESTIONS}
              selectedTags={dislikes}
              onTagsChange={setDislikes}
              maxTags={5}
              conflictTags={loves}
              conflictLabel="Loves"
            />
          </div>

          {/* Gift ideas */}
          <div className="mt-6">
            <p className="text-sm font-bold text-foreground">Gift ideas</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Things they've mentioned wanting or would love to receive.</p>
            <Textarea
              value={giftIdeas}
              onChange={(e) => setGiftIdeas(e.target.value.slice(0, 100))}
              placeholder="e.g. Noise-cancelling headphones, quality notebooks"
              rows={2}
              className="mt-2 rounded-2xl ring-offset-background focus-visible:ring-ring"
            />
            <CharCounter current={giftIdeas.length} max={100} warnAt={80} />
          </div>

          {/* Shared memories */}
          <div className="mt-6">
            <p className="text-sm font-bold text-foreground">Shared memories</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Moments you've shared together.</p>
            <Textarea
              value={sharedMemories}
              onChange={(e) => setSharedMemories(e.target.value.slice(0, 500))}
              placeholder="e.g. Backpacked Vietnam 2019. Was with me when dad was sick."
              rows={3}
              className="mt-2 rounded-2xl ring-offset-background focus-visible:ring-ring"
            />
            <CharCounter current={sharedMemories.length} max={500} warnAt={400} />
          </div>

          {/* Other notes */}
          <div className="mt-6">
            <p className="text-sm font-bold text-foreground">Other notes</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Anything else worth remembering about them.</p>
            <Textarea
              value={otherNotes}
              onChange={(e) => setOtherNotes(e.target.value.slice(0, 300))}
              placeholder="e.g. Career change. Prefers texts over calls."
              rows={3}
              className="mt-2 rounded-2xl ring-offset-background focus-visible:ring-ring"
            />
            <CharCounter current={otherNotes.length} max={300} warnAt={240} />
          </div>
        </div>

        {/* Fixed save button */}
        <div className="absolute bottom-0 inset-x-0">
          <div className="h-8 bg-gradient-to-t from-[hsl(37,26%,91%)] to-transparent" />
          <div className="bg-[hsl(37,26%,91%)] px-5 pb-6">
            <Button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="w-full rounded-full bg-[hsl(0,0%,10%)] text-white hover:bg-[hsl(0,0%,15%)]"
              size="lg"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>

    {/* Unsaved changes exit guard */}
    <Dialog open={showExitGuard} onOpenChange={setShowExitGuard}>
      <DialogContent className="rounded-2xl p-6 sm:max-w-sm [&>button]:hidden">
        <DialogTitle className="text-lg font-bold text-foreground">
          You have unsaved changes
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground mt-2">
          Would you like to save your changes to {firstName} before leaving?
        </DialogDescription>
        <div className="flex flex-col gap-3 mt-4">
          <Button
            onClick={() => {
              setShowExitGuard(false);
              handleSave();
            }}
            className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"
            size="lg"
          >
            Save Changes
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setShowExitGuard(false);
              onOpenChange(false);
            }}
            className="w-full rounded-full"
            size="lg"
          >
            Discard Changes
          </Button>
          <button
            onClick={() => setShowExitGuard(false)}
            className="text-sm font-medium text-accent text-center py-2"
          >
            Keep Editing
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AboutScreen;
